import React from "react";
import { Link } from "react-router-dom";
import { ScanFace, Scale, Ban, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import { GOV_OPTIONS } from "../data/site";

const ICONS = { "scan-face": ScanFace, scale: Scale, ban: Ban };

const MATRIX = [
    { aspect: "Core mechanism", a: "Human confirms every match before action", b: "Statutory rules + external auditor of biometric use", c: "Moratorium on police FRT until safeguards are proven" },
    { aspect: "Fixes false positives?", a: "Yes — at the point of action", b: "Indirectly, via thresholds & audit", c: "Yes — by pausing deployment" },
    { aspect: "Fixes the oversight gap?", a: "No — still police-governed", b: "Yes — independent by design", c: "Partially — resets the clock" },
    { aspect: "Keeps genuine benefits?", a: "Yes, where verification is robust", b: "Yes, conditional on approval", c: "No — foregone during the pause" },
    { aspect: "Main risk", a: "Automation bias", b: "Pace & funding of oversight", c: "Drift or silent reversal" },
    { aspect: "NZ precedent", a: "Foodstuffs two-staff rule (2025)", b: "OPC draft Biometric Code (2024)", c: "2024 policy's own live-use ban" },
];

export default function GovernanceOptions() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-07"
                lines={["Governance", <>options — <i className="text-amber">the three choices</i></>]}
                lede="Our research moves forward on three possible futures for Facial Recognition Technology in New Zealand. Each option answers the same test — could it have prevented the failures of 2018 and 2020, and could it survive the failures documented overseas? — and each carries its own cost. No option is free."
                meta={["SUBJECT: RECOMMENDATION FRAMEWORK"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="7.1"
                        kicker="The options"
                        title="Three paths, one decision"
                        lede="Select a file to open the full analysis of each option — its mechanism, its evidence base, and the case against it."
                    />
                    <div className="grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {GOV_OPTIONS.map((o, i) => {
                            const Icon = ICONS[o.icon];
                            return (
                                <Reveal key={o.slug} delay={i * 0.08} className="bg-ink">
                                    <Link
                                        to={`/governance-options/${o.slug}`}
                                        data-testid={`governance-option-${o.slug}`}
                                        className="group block p-7 sm:p-8 h-full hover:bg-sheet transition-colors"
                                    >
                                        <div className="flex items-center justify-between mb-8">
                                            <Icon size={24} className="text-amber" />
                                            <span className="font-mono text-[11px] text-ash">{o.no}</span>
                                        </div>
                                        <h3 className="font-display text-2xl sm:text-3xl text-paper group-hover:text-amber transition-colors mb-4">
                                            {o.title}
                                        </h3>
                                        <p className="font-display italic text-paper/70 mb-4">{o.tagline}</p>
                                        <p className="text-sm leading-relaxed text-ash mb-6">{o.short}</p>
                                        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber">
                                            Open file <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="7.2"
                        kicker="Comparison"
                        title="The options, side by side"
                        lede="A summary matrix. Columns A–C correspond to Option 1, 2 and 3 above."
                    />
                    <Reveal>
                        <div className="overflow-x-auto border border-white/10" data-testid="governance-comparison-table">
                            <table className="w-full min-w-[720px] text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-white/10 bg-ink">
                                        <th className="font-mono text-[10px] uppercase tracking-[0.25em] text-ash p-4 sm:p-5 w-[18%]">Aspect</th>
                                        <th className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber p-4 sm:p-5">
                                            07.1 Human Verification
                                        </th>
                                        <th className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber p-4 sm:p-5">
                                            07.2 Independent Oversight
                                        </th>
                                        <th className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber p-4 sm:p-5">
                                            07.3 Temporary Restriction
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {MATRIX.map((row, i) => (
                                        <tr key={row.aspect} className={`border-b border-white/10 last:border-0 ${i % 2 ? "bg-ink/60" : "bg-ink"}`}>
                                            <td className="p-4 sm:p-5 text-sm text-paper font-medium">{row.aspect}</td>
                                            <td className="p-4 sm:p-5 text-sm text-ash leading-relaxed">{row.a}</td>
                                            <td className="p-4 sm:p-5 text-sm text-ash leading-relaxed">{row.b}</td>
                                            <td className="p-4 sm:p-5 text-sm text-ash leading-relaxed">{row.c}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-6 mt-12">
                        <Reveal>
                            <div className="flex gap-4 border border-white/10 p-6">
                                <CheckCircle2 size={18} className="text-match shrink-0 mt-1" />
                                <p className="text-sm leading-relaxed text-ash">
                                    <span className="text-paper">What the options share:</span> all three accept that the
                                    2018–2020 record shows governance cannot be optional, and that the Privacy
                                    Commissioner's amber-light conditions are the floor, not the ceiling.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="flex gap-4 border border-white/10 p-6">
                                <XCircle size={18} className="text-alert shrink-0 mt-1" />
                                <p className="text-sm leading-relaxed text-ash">
                                    <span className="text-paper">Where they diverge:</span> on whether the problem is
                                    best fixed inside the process (verification), outside it (oversight), or by
                                    stopping it (restriction) until the other two are possible.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
